
import { BaseProvider } from './BaseProvider';
import { ProviderType, ProviderModel, GenerateRequest, GenerateResponse, PROVIDER_DEFAULTS, StreamEvent } from './types';
import { pipeline, env, PipelineType, TextStreamer } from '@huggingface/transformers';
import { logger } from '../loggingService';

// Configure environment for browser
env.allowLocalModels = false;
env.useBrowserCache = true;

// Define Liquid LFM 2.5 ONNX model ID
const LIQUID_MODEL_ID = 'LiquidAI/LFM2.5-1.2B-Thinking-ONNX';

export class OnnxProvider extends BaseProvider {
    readonly type: ProviderType = 'onnx';
    readonly name = 'ONNX Support (Edge)';
    readonly supportsStreaming = true; // Transformers.js supports callbacks which we can adapt
    readonly supportsFunctionCalling = false;
    readonly requiresApiKey = false;

    private gpuPreference: 'high-performance' | 'low-power' = 'high-performance';

    constructor() {
        super();
        // Load preference from storage if available
        const stored = localStorage.getItem('cc_gpu_preference');
        if (stored === 'low-power' || stored === 'high-performance') {
            this.gpuPreference = stored;
        }
    }

    setLoadProgressCallback(callback: (progress: number, task: string) => void) {
        this.loadProgressCallback = callback;
    }

    setGpuPreference(pref: 'high-performance' | 'low-power') {
        this.gpuPreference = pref;
        // If generator exists, we might need to reload, but for now just set it for next load
        if (this.generator) {
            console.warn("GPU Preference changed. Model reload required to apply.");
        }
    }

    async testConnection(): Promise<{ success: boolean; message: string; models?: ProviderModel[] }> {
        // For ONNX, "testing connection" checks if WebGPU is available and if the model is cached/loadable.
        try {
            if (!navigator.gpu) {
                return { success: false, message: 'WebGPU is not supported in this browser.' };
            }

            // We don't actually load the full model here as it's heavy (2GB+).
            // We just report success if the environment looks good.
            // Use isModelLoaded to check specifically.

            return {
                success: true,
                message: 'WebGPU is supported. Model ready to load.',
                models: [{
                    id: LIQUID_MODEL_ID,
                    name: 'Liquid LFM 2.5 1.2B (Thinking)',
                    description: 'On-Device WebGPU Model'
                }]
            };

        } catch (e: any) {
            return { success: false, message: `WebGPU check failed: ${e.message}` };
        }
    }

    async listModels(): Promise<ProviderModel[]> {
        return [{
            id: LIQUID_MODEL_ID,
            name: 'Liquid LFM 2.5 1.2B (Thinking)',
            description: 'On-Device WebGPU Model',
            contextLength: 4096 // Approximate
        }];
    }

    async loadModel() {
        if (this.generator) return;
        this.isModelLoading = true;

        try {
            logger.info('OnnxProvider', `Loading model ${LIQUID_MODEL_ID} with preference: ${this.gpuPreference}...`);

            // Use 'text-generation' pipeline
            this.generator = await pipeline('text-generation', LIQUID_MODEL_ID, {
                device: 'webgpu', // Transformers.js v3 uses specific device object or string
                // @ts-ignore - device option in v3 can take object for powerPreference
                session_options: {
                    executionProviders: [{
                        name: 'webgpu',
                        deviceOptions: {
                            powerPreference: this.gpuPreference
                        }
                    }]
                },
                dtype: 'q4', // Quantized for browser
                progress_callback: (x: any) => {
                    if (this.loadProgressCallback) {
                        // x is usually { status: 'progress', name: 'file', file: '...', progress: 0-100, loaded: bytes, total: bytes }
                        // Map to simple 0-100
                        if (x.status === 'progress') {
                            this.loadProgressCallback(x.progress || 0, `Downloading ${x.file}`);
                        } else if (x.status === 'initiate') {
                            this.loadProgressCallback(0, `Starting ${x.file}`);
                        } else if (x.status === 'done') {
                            this.loadProgressCallback(100, `Loaded ${x.file}`);
                        }
                    }
                }
            });

            logger.info('OnnxProvider', 'Model Loaded Successfully');
        } catch (e) {
            logger.error('OnnxProvider', 'Failed to load model', e);
            throw e;
        } finally {
            this.isModelLoading = false;
        }
    }

    isLoaded(): boolean {
        return !!this.generator;
    }

    async generate(request: GenerateRequest): Promise<GenerateResponse> {
        if (!this.generator) {
            await this.loadModel();
        }

        // Construct prompt using a simple template if automatic one fails
        // Liquid LFM 2.5 seems to work best with simple formatted strings or standard ChatML if the tokenizer supports it.
        // Let's try to use the tokenizer's apply_chat_template if available via the pipeline, 
        // BUT if we get raw JSON back, it means we passed the object and it echoed it.

        try {
            // Option 1: Pass messages array. 
            // If this returns the full JSON, we need to extract the LAST message.
            const output = await this.generator(request.messages, {
                max_new_tokens: request.maxTokens || 1024,
                temperature: request.temperature || 0.7,
                do_sample: true,
                return_full_text: false, // Try to suppress input
            });

            // Log output to debug the structure
            console.log('OnnxProvider Raw Output:', output);

            let textFn = '';
            // Analysis of the User's Screenshot:
            // It shows `[{"role":"system", ...`. This is the INPUT.
            // This means `return_full_text: false` might be ignored or the pipeline returns the full conversation.

            const gen = output[0]?.generated_text;

            if (Array.isArray(gen)) {
                // It returned the full conversation array. 
                // The LAST item should be the new assistant message.
                const lastMsg = gen[gen.length - 1];
                if (lastMsg.role === 'assistant') {
                    textFn = lastMsg.content;
                } else {
                    textFn = JSON.stringify(lastMsg);
                }
            } else if (typeof gen === 'object' && gen !== null) {
                // Single object return
                if (gen.role === 'assistant' && gen.content) {
                    textFn = gen.content;
                } else if (gen.content) {
                    textFn = gen.content;
                } else {
                    textFn = JSON.stringify(gen);
                }
            } else {
                textFn = String(gen || '');
            }

            // CLEANUP: If the model hallucinates the prompt format in the response
            if (textFn.trim().startsWith('[{"role":')) {
                try {
                    const parsed = JSON.parse(textFn);
                    if (Array.isArray(parsed)) {
                        const last = parsed[parsed.length - 1];
                        if (last.content) textFn = last.content;
                    }
                } catch (e) {
                    // Not valid JSON, just raw text that looks like it
                }
            }

            return {
                text: textFn,
                model: LIQUID_MODEL_ID
            };

        } catch (e: any) {
            logger.error('OnnxProvider', 'Generation failed', e);
            throw e;
        }
    }

    // Streaming support - True streaming using TextStreamer
    async *generateStream(request: GenerateRequest): AsyncGenerator<StreamEvent, void, unknown> {
        if (!this.generator) {
            await this.loadModel();
        }

        const tokenQueue: string[] = [];
        let resolveData: (() => void) | null = null;
        let dataPromise = new Promise<void>(r => resolveData = r);
        let isDone = false;
        let error: any = null;

        // Create TextStreamer to capture output
        const streamer = new TextStreamer(this.generator.tokenizer, {
            skip_prompt: true,
            skip_special_tokens: true,
            callback_function: (text: string) => {
                tokenQueue.push(text);
                if (resolveData) resolveData();
                dataPromise = new Promise<void>(r => resolveData = r);
            }
        });

        // Start generation in background
        const messages = request.systemPrompt
            ? [{ role: 'system', content: request.systemPrompt }, ...request.messages]
            : request.messages;

        (async () => {
            try {
                await this.generator(messages, {
                    max_new_tokens: request.maxTokens || 4096,
                    temperature: request.temperature || 0.7,
                    do_sample: true,
                    return_full_text: false,
                    streamer: streamer,
                });
            } catch (e) {
                console.error("Background Stream Error", e);
                error = e;
            } finally {
                isDone = true;
                if (resolveData) resolveData();
            }
        })();

        // Yield loop
        while (!isDone || tokenQueue.length > 0) {
            if (error) {
                yield { type: 'error', error: error.message || String(error) };
                return;
            }

            if (tokenQueue.length > 0) {
                const token = tokenQueue.shift();
                if (token) {
                    yield { type: 'text', content: token };
                }
            } else {
                await dataPromise;
            }
        }
    }
}
